// import {extent as d3_extent} from 'd3-array'
import { select as d3_select } from 'd3-selection'
import IconIncreaseTextSize from '../styles/icons/icon-increase-text-size.svg'
import IconDarkMode from '../styles/icons/icon-dark-mode.svg'
import IconContrast from '../styles/icons/icon-contrast.svg'
import IconGuideLines from '../styles/icons/icon-guide-lines.svg'
import AccessibilityIcon from '../styles/icons/icon-accessibility.svg'
import { ACCESSIBILITY_OPTIONS_KEYS } from '../utils/accessibilityUtils'
import { isDarkMode } from '../utils/configUtils'
import { SKIN_BACKGROUND, SKIN_BACKGROUND_DARK } from '../utils/colorUtils'
import tippy from 'tippy.js'
import { LOCALE_CONFIGS } from '../utils/localeUtils'

let addedTippyStyles = false

function AccessibilityBehavior($root, chart, config = {}) {
  const ACCESSIBILITY_OPTIONS = [
    /*
    {
      id: ACCESSIBILITY_OPTIONS_KEYS.contrast,
      svg: IconContrast,
    },
    */
    {
      id: ACCESSIBILITY_OPTIONS_KEYS.darkMode,
      svg: IconDarkMode,
      tooltip: 'tooltipTheme',
    },
    {
      id: ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize,
      svg: IconIncreaseTextSize,
      tooltip: 'tooltipTypeSize',
    },
    {
      id: ACCESSIBILITY_OPTIONS_KEYS.guideLines,
      svg: IconGuideLines,
      tooltip: 'tooltipGuideLines',
    },
  ]
  const OPTION_PREFIX = 'accessibility-option'

  function addTippyStyles() {
    const tippyStyles = `
    .tippy-box[data-animation=fade][data-state=hidden] {
      opacity: 0;
    }
    [data-tippy-root] {
      max-width: calc(100vw - 10px);
    }
    .tippy-box {
      position: relative;
      background-color: #fff;
      color: #888888;
      border-radius: 4px;
      font-size: 14px;
      font-family: santander-microtext-embedded;
      line-height: 1.4;
      white-space: normal;
      outline: 0;
      transition-property: transform, visibility, opacity;
       box-shadow: 
    0 0 20px 4px rgba(154, 161, 177, 0.15),
    0 4px 80px -8px rgba(36, 40, 47, 0.25),
    0 4px 4px -2px rgba(91, 94, 105, 0.15);
    }
    .tippy-svg-arrow {
      fill: #fff;
    }
    .tippy-box[data-placement^=top] > .tippy-arrow {
      bottom: 0;
    }
    .tippy-box[data-placement^=top] > .tippy-arrow:before {
      bottom: -7px;
      left: 0;
      border-width: 8px 8px 0;
      border-top-color: #fff;
      transform-origin: center top;
    }
    .tippy-box[data-placement^=bottom] > .tippy-arrow {
      top: 0;
    }
    .tippy-box[data-placement^=bottom] > .tippy-arrow:before {
      top: -7px;
      left: 0;
      border-width: 0 8px 8px;
      border-bottom-color: #fff;
      transform-origin: center bottom;
    }
    .tippy-box[data-placement^=left] > .tippy-arrow {
      right: 0;
    }
    .tippy-box[data-placement^=left] > .tippy-arrow:before {
      border-width: 8px 0 8px 8px;
      border-left-color: #fff;
      right: -7px;
      transform-origin: center left;
    }
    .tippy-box[data-placement^=right] > .tippy-arrow {
      left: 0;
    }
    .tippy-box[data-placement^=right] > .tippy-arrow:before {
      left: -7px;
      border-width: 8px 8px 8px 0;
      border-right-color: #fff;
      transform-origin: center right;
    }
    .tippy-box[data-inertia][data-state=visible] {
      transition-timing-function: cubic-bezier(.54, 1.5, .38, 1.11);
    }
    .tippy-arrow {
      width: 16px;
      height: 16px;
      color: #fff;
    }
    .tippy-arrow:before {
      content: "";
      position: absolute;
      border-color: transparent;
      border-style: solid;
    }
    .tippy-content {
      position: relative;
      padding: 5px 9px;
      z-index: 1;
    }
    
    .tippy-backdrop {
      background-color: #fff;
    }
  `
    const styleSheet = document.createElement('style')
    styleSheet.type = 'text/css'
    styleSheet.innerText = tippyStyles
    document.head.appendChild(styleSheet)
  }

  if (!addedTippyStyles) {
    addTippyStyles()
    addedTippyStyles = true
  }

  //Draw container
  const drawAccessibilityDropdownContent = (root, show) => {
    let accessibilityDropdownContent = d3_select(root)
      .selectAll('.accessibility-dropdown-content')
      .data(show ? [null] : [])
    const accessibilityDropdownContentEnter = accessibilityDropdownContent
      .enter()
      .append('div')
      .attr('class', 'accessibility-dropdown-content')
      .style('position', 'absolute')
      .style('top', '0')
      .style('left', '-4px')
      .style('display', 'flex')
      .style('flex-direction', 'row')
      .style('border-radius', '2px')
      .style('border', '1px solid #DEEDF2')
      .style('transform', 'translate(-100%, 0)')
      .style('height', '100%')
      .style('width', '0')
      .style('overflow', 'hidden')
      .style('opacity', 0)
      .on('click', e => e.stopPropagation())

    accessibilityDropdownContent
      .exit()
      .transition()
      .duration(500)
      .style('width', '0px')
      .style('opacity', 0)
      .remove()
    accessibilityDropdownContent = accessibilityDropdownContent.merge(
      accessibilityDropdownContentEnter
    )

    //Draw options
    let options = accessibilityDropdownContent
      .selectAll('.accessibility-option')
      .data(ACCESSIBILITY_OPTIONS)
    const optionsEnter = options
      .enter()
      .append('div')
      .attr('id', d => `${OPTION_PREFIX}-${d.id}`)
      .attr('class', 'accessibility-option')
      .style('cursor', 'pointer')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('padding', '6px')
      .style('border-right', (d, i, arr) => (i < arr.length - 1 ? '1px solid #CEDEE7' : 'none'))
      .on('click', function (e, d) {
        e.preventDefault()
        e.stopPropagation()
        chart._parent.accessibilityOptions = {
          ...chart._parent.accessibilityOptions,
          [d.id]: !chart._parent.accessibilityOptions[d.id],
        }
        const selectedColor = 'unset'
        const defaultColor = isDarkMode(chart.config, chart._parent.accessibilityOptions)
          ? 'unset'
          : '#F5F9FB'
        this.style.backgroundColor = chart._parent.accessibilityOptions[d.id]
          ? selectedColor
          : defaultColor
      })
      .html(d => d.svg)
    options.exit().remove()
    options = options.merge(optionsEnter)
    options.style('background-color', d => {
      const selectedColor = 'unset'
      const defaultColor = isDarkMode(chart.config, chart._parent.accessibilityOptions)
        ? 'unset'
        : '#F5F9FB'
      return chart._parent.accessibilityOptions[d.id] ? selectedColor : defaultColor
    })
    options
      .select('svg')
      .attr('height', '20px')
      .attr('width', '20px')
      .attr('fill', d => {
        return isDarkMode(chart.config, chart._parent.accessibilityOptions) &&
          chart._parent.accessibilityOptions[d.id]
          ? '#FFFFFF'
          : '#727272'
      })

    options.each(function (d) {
      const element = d3_select(this)
      const id = `${OPTION_PREFIX}-${d.id}`
      const tooltipContenti18n = LOCALE_CONFIGS[chart.config.locale][d.tooltip]

      const existingInstance = element.node()._tippy
      if (existingInstance) {
        existingInstance.setContent(tooltipContenti18n)
      } else {
        tippy(`#${id}`, {
          content: tooltipContenti18n,
          theme: 'light',
          placement: 'bottom',
          animation: 'fade',
        })
      }
    })

    //Wait to have all the options set to know the total container width
    accessibilityDropdownContentEnter
      .transition(500)
      .style('width', function () {
        return `${this.scrollWidth + 2}px`
      })
      .style('opacity', 1)
  }
  const drawAccessibilityDropdown = () => {
    let accessibilityDropdown = d3_select($root)
      .selectAll('.accessibility-dropdown')
      .data(config.showAccessibilityOptions ? [null] : [])
    const accessibilityDropdownEnter = accessibilityDropdown
      .enter()
      .append('div')
      .attr('class', 'accessibility-dropdown')
      .style('position', 'relative')
      .style('display', 'flex')
    accessibilityDropdown.exit().remove()
    accessibilityDropdown = accessibilityDropdown.merge(accessibilityDropdownEnter)

    let accessibilityActionButton = accessibilityDropdown
      .selectAll('.accessibility-action-button')
      .data([null])

    const accessibilityActionButtonEnter = accessibilityActionButton
      .enter()
      .append('div')
      .attr('class', 'accessibility-action-button')
      .style('padding', '8px')
      .style('border-radius', '1px')
      .style('border', '1px solid #CEDEE7')
      .style('background-color', '#F5F9FB')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('cursor', 'pointer')
      .on('click', function () {
        const isSelected =
          !this.getAttribute('selected') || this.getAttribute('selected') === 'false'
        this.setAttribute('selected', isSelected)
        const selectedColor = SKIN_BACKGROUND
        const defaultColor = '#FFFFFF'
        this.style.backgroundColor = isSelected ? selectedColor : defaultColor
        drawAccessibilityDropdown()
      })
      .html(AccessibilityIcon)
    accessibilityActionButton.exit().remove()
    accessibilityActionButton = accessibilityActionButton.merge(accessibilityActionButtonEnter)

    const accessibilityActionButtonNode = accessibilityActionButton.node()
    if (accessibilityActionButtonNode) {
      const isDropdownSelected =
        !!accessibilityActionButtonNode.getAttribute('selected') &&
        accessibilityActionButtonNode.getAttribute('selected') === 'true'

      drawAccessibilityDropdownContent(accessibilityActionButtonNode, isDropdownSelected)
    }
  }

  drawAccessibilityDropdown()
}

AccessibilityBehavior.defaultConfig = {
  index: 204,
  /**
   * {-EN-} Brief description of the chart for accessibility and search engine optimization.
   * {-ES-} Breve descripción del gráfico para accesibilidad y optimización en motores de búsqueda.
   */
  altTitle: {
    type: String,
    default: '',
    exposed: true,
  },
  /**
   * {-EN-} Detailed description of the chart for accessibility and search engine optimization.
   * {-ES-} Descripción detallada del gráfico para accesibilidad y optimización en motores de búsqueda.
   */
  altDesc: {
    type: String,
    default: '',
    exposed: true,
  },
  /**
   * {-EN-} Display a dropdown with different accesibility options.
   * {-ES-} Mostrar un menú desplegable con diferentes opciones de accesibilidad.
   */
  showAccessibilityOptions: {
    type: Boolean,
    default: false,
    exposed: true,
  },
}
AccessibilityBehavior.configName = 'AccessibilityBehavior'

export default AccessibilityBehavior
